import { resetAnalytics } from "@/features/analytics/posthog";
import { useGlobalEffect } from "@/features/utils/use-global-effect";
import { useGlobalState } from "@/features/utils/use-global-state";
import * as Sentry from "@sentry/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef } from "react";
import { AuthAPI } from "../api/auth";
import { getToken } from "../jwt";
import { userKeys, useSetCurrentUser, useUser, type User } from "./use-user";

export const useAuth = () => {
  const currentUser = useUser();
  const { setUser, clearUser } = useSetCurrentUser();
  const login = useLogin();
  const logout = useLogout();
  const { renewToken, isRenewing } = useAutoTokenRenewal();

  // Track initialization state
  const [isInitializing, setIsInitializing] = useGlobalState("init-auth", true);
  const tokenRenewalTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Initialize auth state from localStorage on mount
  useGlobalEffect(
    "useAuth",
    async () => {
      const token = getToken();
      if (token) {
        // Try to renew the token to verify it's still valid and get user data
        try {
          await renewToken();
        } catch (e) {
          console.error("Token renewal failed during initialization", e);
        }
      }
      await new Promise((resolve) => setTimeout(resolve, 500));
      setIsInitializing(false);
    },
    [],
  );

  // Pre-declare handleLogout for useAutoLogout circular dependency
  const handleLogoutRef = useRef<() => Promise<void>>(async () => {});

  const handleLogout = useCallback(async () => {
    try {
      await logout.mutateAsync();
      clearUser();
      if (tokenRenewalTimerRef.current)
        clearInterval(tokenRenewalTimerRef.current);
    } catch (error) {
      // Even if logout fails, clear local state
      clearUser();
      throw error;
    } finally {
      Sentry.setUser(null);
      resetAnalytics();
      document.location.reload(); // Safest way to reset the app state
    }
  }, [logout, clearUser]);

  // Update the ref with the actual function
  handleLogoutRef.current = handleLogout;

  // Auto token renewal every 10 minutes
  useEffect(() => {
    if (!currentUser.data?.id) return;

    const renewTokenPeriodically = () => {
      const token = getToken();
      if (token) {
        renewToken();
      }
    };

    // Renew token every 10 minutes
    const timer = setInterval(renewTokenPeriodically, 10 * 60 * 1000);
    tokenRenewalTimerRef.current = timer;

    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
  }, [currentUser.data?.id, renewToken]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      const tokenRenewalTimer = tokenRenewalTimerRef.current;
      if (tokenRenewalTimer) clearInterval(tokenRenewalTimer);
    };
  }, []);

  const handleLogin = async (preSignedTokens: string[]) => {
    try {
      const result = await login.mutateAsync({ preSignedTokens });
      if (result.token && result.user) {
        setUser(result.user as User);

        // Identify user in Sentry
        Sentry.setUser({
          id: result.user.id,
          email: result.user.email,
          username: result.user.email,
          role: result.user.role,
          preferences: result.user.preferences,
        });
        await new Promise((resolve) => setTimeout(resolve, 100)); // Make sure setUser has propagated
      }
      return result;
    } catch (error) {
      clearUser();
      throw error;
    }
  };

  return {
    isInitializing,
    isLoading: currentUser.isLoading || login.isPending || isRenewing,
    isAuthenticated: !!currentUser.data?.id && !!getToken(),
    login: handleLogin,
    logout: handleLogout,
    loginError: login.error,
    logoutError: logout.error,
  };
};

// Types
export interface LoginRequest {
  preSignedTokens: string[];
}

export interface LoginResponse {
  success: boolean;
  token: string;
  user: Partial<User> & {
    id: string;
    email: string;
    role: string;
    preferences: {
      lang: string;
    };
  };
  message?: string;
}

export interface TokenRenewalRequest {
  token: string;
}

// Login Hook
export function useLogin() {
  return useMutation<LoginResponse, Error, LoginRequest>({
    mutationFn: AuthAPI.login,
    onSuccess: (data) => {
      localStorage.setItem("auth_token", data.token);
    },
  });
}

// Token Renewal Hook
export function useTokenRenewal() {
  const { setUser } = useSetCurrentUser();
  return useMutation({
    mutationFn: AuthAPI.renewToken,
    onSuccess: (data) => {
      if (data.user) {
        setUser(data.user as User);

        // Update Sentry user context after token renewal
        Sentry.setUser({
          id: data.user.id,
          email: data.user.email,
          username: data.user.email,
          role: data.user.role,
          preferences: data.user.preferences,
        });
      }
    },
  });
}

// Logout Hook
export function useLogout() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, void>({
    mutationFn: async () => {
      // Clear local authentication state
      try {
        await AuthAPI.logout();
      } finally {
        localStorage.removeItem("auth_token");
        // Refresh user state
        queryClient.invalidateQueries({ queryKey: userKeys.all });
      }
    },
  });
}

// Helper hook to automatically renew token
export function useAutoTokenRenewal() {
  const tokenRenewal = useTokenRenewal();

  const renewToken = async () => {
    const currentToken = getToken();
    if (currentToken) {
      await tokenRenewal.mutateAsync({ token: currentToken });
    }
  };

  return {
    renewToken,
    isRenewing: tokenRenewal.isPending,
    error: tokenRenewal.error,
  };
}
